<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'WorkSummaryViewFilters',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'submit']);

  const props = defineProps({});

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const item = ref({
    date_from: null,
    date_to: null,
  });
  const ux = ref([]);
  const b_8564662037462133_style = computed(() => ({
    width: '500px',
  }));

  const $data = computed(() => ({
    item: item.value,
    ux: ux.value,
    b_8564662037462133_style: b_8564662037462133_style.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item', cache: { type: 'local', key: 'WorkSummaryViewFilters' } },
      { name: 'ux' },
      { name: 'b_8564662037462133_style', nodeId: '8564662037462133' },
    ],
    loaders: {},
    ux: {},
    dataRefs: { item, ux, b_8564662037462133_style },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const act_8564662037462133_setData_cchjjsco6_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.date_from',
      value: $event.data[0],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_8564662037462133_setData_ow0m6xvwc_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item.date_to',
      value: $event.data[1],
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_3943151158818434_emit_ugvolttmj_getActionArgs = async ($event) => {
    return {
      event: 'submit',
      value: item.value,
    };
  };

  const act_7315092382398562_reloadComponentData_6ngad5z02 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_ssa34lvpv =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_6ngad5z02,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_ssa34lvpv],
    event: 'reload',
  });
  const act_8564662037462133_setData_cchjjsco6 = new actionTypes.SetDataAction({
    actionArgs: act_8564662037462133_setData_cchjjsco6_getActionArgs,
  });
  const inst_evh_8564662037462133_changed_dc0v7nkpn =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_setData_cchjjsco6,
      event: 'changed',
      displayName: 'setData',
    });
  const act_8564662037462133_setData_ow0m6xvwc = new actionTypes.SetDataAction({
    actionArgs: act_8564662037462133_setData_ow0m6xvwc_getActionArgs,
  });
  const inst_evh_8564662037462133_changed_o9bow5bn3 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_setData_ow0m6xvwc,
      event: 'changed',
      displayName: 'setData',
    });
  const inst_evh_8564662037462133_changed = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_8564662037462133_changed_dc0v7nkpn,
      inst_evh_8564662037462133_changed_o9bow5bn3,
    ],
    event: 'changed',
  });
  const act_3943151158818434_emit_ugvolttmj = new actionTypes.EmitAction({
    actionArgs: act_3943151158818434_emit_ugvolttmj_getActionArgs,
    displayName: 'load',
  });
  const inst_evh_3943151158818434_clickPreventStop_8efvimuxu =
    new actionTypes.EventHandlerImpl({
      action: act_3943151158818434_emit_ugvolttmj,
      event: 'click.prevent.stop',
      displayName: 'load',
    });
  const inst_evh_3943151158818434_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3943151158818434_clickPreventStop_8efvimuxu],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_ssa34lvpv,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_changed_dc0v7nkpn,
    inst_evh_8564662037462133_changed_o9bow5bn3,
    inst_evh_8564662037462133_changed,
    inst_evh_3943151158818434_clickPreventStop_8efvimuxu,
    load: inst_evh_3943151158818434_clickPreventStop_8efvimuxu,
    inst_evh_3943151158818434_clickPreventStop,
  };

  const evh_7315092382398562_reload_ssa34lvpv = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_ssa34lvpv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_changed_dc0v7nkpn = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_changed_dc0v7nkpn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_changed_o9bow5bn3 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_changed_o9bow5bn3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_changed = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_changed.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3943151158818434_clickPreventStop_8efvimuxu = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3943151158818434_clickPreventStop_8efvimuxu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3943151158818434_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_3943151158818434_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpBox v-if="loaded">
      <DateRangeSelector
        name="paymentFilters"
        :from="item.date_from"
        :to="item.date_to"
        class="me-2"
        :style="b_8564662037462133_style"
        @changed="evh_8564662037462133_changed($event, {})"
      >
      </DateRangeSelector>
      <div>
        <ItpButton
          variant="light"
          :icon="$config.reloadIcon"
          :title="pgettext('WorkSummaryViewFilters', 'Load data')"
          :spinning="actions && actions.load && actions.load.isRunning"
          @click.prevent.stop="
            evh_3943151158818434_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </div>
    </ItpBox>
  </ItpUsercontrol>
</template>
